import { Button } from '@/components/shared/buttons/button';
import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { Text } from '@/components/shared/element/text';
import { Flex } from '@/components/shared/layout/flex';
import { formatPrice } from '@/components/shared/utility/format';
import { styled } from '@/stitches.config';
import { IItem, IMy24Theme, IMy24Tracking } from '@/types/shared';

const StyledContent = styled('div', {});

const BadgeItems = styled(Flex, {
  textAlign: 'center',
  variants: {
    animStart: {
      true: {
        '> *': {
          opacity: 0,
          transform: 'translate(0, 20px)',
          backfaceVisibility: 'hidden',
          willChange: 'transform, opacity',
          '&:nth-child(1)': {
            transitionDelay: '0.5s',
          },
          '&:nth-child(2)': {
            transitionDelay: '0.6s',
          },
          '&:nth-child(3)': {
            transitionDelay: '0.7s',
          },
        },
      },
    },
    animate: {
      true: {
        '> *': {
          opacity: 1,
          transform: 'translate(0, 0)',
          transitionProperty: 'opacity, transform',
          transitionDuration: '0.6s',
          transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
        },
      },
    },
  },
});

const BadgeItem = styled(Flex, {
  flexGrow: 1,
  flexBasis: 0,
  maxWidth: 160,
});

const BadgeTitle = styled(Flex, {
  height: '100%',
});

const BadgeButtonWrap = styled(ButtonGroup, {
  marginTop: '$space-5',
  '@lg': {
    marginTop: '$space-6',
  },
  '> *:not(:first-child)': {
    '@maxsm': {
      display: 'none',
    },
  },
  variants: {
    animStart: {
      true: {
        '> *': {
          opacity: 0,
          transform: 'translate(0, 20px)',
          backfaceVisibility: 'hidden',
          willChange: 'transform, opacity',
          '&:nth-child(1)': {
            transitionDelay: '0.8s',
          },
          '&:nth-child(2)': {
            transitionDelay: '0.9s',
          },
        },
      },
    },
    animate: {
      true: {
        '> *': {
          opacity: 1,
          transform: 'translate(0, 0)',
          transitionProperty: 'opacity, transform',
          transitionDuration: '0.6s',
          transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
        },
      },
    },
  },
});

interface IProps extends React.ComponentProps<typeof StyledContent> {
  css?: any;
  className?: string;
  theme?: IMy24Theme;
  item?: IItem;
  dataTest?: string;
  dataTrack?: IMy24Tracking;
  mount?: boolean;
  animStart?: boolean;
}

export const BadgesWithButton: React.FC<IProps> = ({
  theme = 'dark',
  mount = false,
  ...props
}) => {
  return (
    <StyledContent css={props?.css} className={props?.className}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {props?.item?.summary?.length > 0 && (
        <BadgeItems
          justifyContent="center"
          columnGap="1"
          data-test={`${props?.dataTest}_items`}
          animStart={props?.animStart}
          animate={mount}
        >
          {props?.item?.summary?.map((sum, i) => (
            <BadgeItem
              key={i}
              direction="column"
              gap="1"
              data-test={`${props?.dataTest}_item_${i + 1}`}
              className={`${props?.dataTest}-item-${i + 1}`}
            >
              {!!sum?.preTitle && (
                <Text
                  size="text-body-2"
                  variant={
                    theme === 'dark' ? 'text-white' : 'text-gray-darkest'
                  }
                  data-test={`${props?.dataTest}_item_preTitle`}
                >
                  {sum?.preTitle}
                </Text>
              )}
              {!!sum?.title && (
                <BadgeTitle justifyContent="center" alignItems="center">
                  <Text
                    size="text-body-2"
                    font="bold"
                    variant={
                      theme === 'dark' ? 'text-white' : 'text-gray-darkest'
                    }
                    data-test={`${props?.dataTest}_item_preTitle`}
                    wrap
                    dangerouslySetInnerHTML={{
                      __html:
                        typeof sum?.title === 'number'
                          ? formatPrice(sum?.title, true)
                          : sum?.title,
                    }}
                  />
                </BadgeTitle>
              )}
            </BadgeItem>
          ))}
        </BadgeItems>
      )}
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {props?.item?.buttons?.length > 0 && (
        <BadgeButtonWrap
          justify="center"
          wrap="noWrap"
          animStart={props?.animStart}
          animate={mount}
          data-myi-hide
        >
          {props?.item?.buttons?.map((button, i) => (
            <Button
              key={i}
              href={button?.url}
              onClick={button?.onClick}
              target={button?.target}
              border={button?.border}
              label={button?.label}
              icon={button?.icon}
              variant={button?.color}
              size={button?.size}
              isMinWidth
              tracking={{
                dataTrack: props?.dataTrack as string,
                dataTrackSection: props?.dataTest,
                dataTrackText: button?.label,
                dataTrackValue: props?.item?.title,
                dataTrackUrl: button?.url,
              }}
              test={{
                dataTest: `${props?.dataTest}_ctaButton_${i + 1}`,
              }}
              className={`${props?.dataTest}-button-${i + 1}`}
            />
          ))}
        </BadgeButtonWrap>
      )}
    </StyledContent>
  );
};

BadgesWithButton.displayName = 'BadgesWithButton';
